import type { ChartOptions, Scale } from 'chart.js/auto';
import type { AnnotationOptions } from 'chartjs-plugin-annotation';
import type { Context } from 'chartjs-plugin-datalabels';
import { PSD_CALC_MODE } from 'containers/FlowProperties/types';
import theme from 'theme/theme';

interface LabelValue {
  x: number;
  y: number;
  interpolated?: boolean;
}

interface LineChartOptionsInterface {
  lowerLimit?: number;
  upperLimit?: number;
  smallestX: number | undefined;
  biggestX: number | undefined;
  annotations?: AnnotationOptions[];
  showGridLines: boolean;
  psdCalcMode: string;
  metricType: { value: string };
  setLimits: (lower: number, upper: number) => void;
  ticks?: number[];
  enableWheelZoom?: boolean;
  enablePan?: boolean;
}

export const getLineOptions = ({
  lowerLimit,
  upperLimit,
  smallestX,
  biggestX,
  annotations,
  showGridLines,
  psdCalcMode,
  metricType,
  setLimits,
  ticks,
  enableWheelZoom = true,
  enablePan = true,
}: LineChartOptionsInterface): ChartOptions<'line'> => {
  const xAxisTicks = getXAxisLabels(lowerLimit ?? 0, upperLimit ?? 100, ticks);

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      padding: {
        right: 40,
      },
    },
    scales: {
      y: {
        type: 'linear',
        grid: {
          display: showGridLines,
        },
        title: {
          display: true,
          text:
            psdCalcMode === PSD_CALC_MODE.PERCENT
              ? 'Percent [%]'
              : 'Mass [mtph]',
        },
        beginAtZero: true,
      },
      x: {
        type: 'logarithmic',
        grid: {
          display: showGridLines,
        },
        title: {
          display: true,
          text: metricType.value === 'IMPERIAL' ? 'Size [in]' : 'Size [mm]',
        },
        min: ticks ? xAxisTicks.at(0) : undefined,
        max: ticks ? xAxisTicks.at(-1) : undefined,
        ticks: {
          callback: (value: number | string) => ` ${value} `,
        },
        afterBuildTicks: (axis: Scale) => {
          axis.ticks = xAxisTicks.map((v) => ({ value: v }));
        },
      },
    },
    hover: { mode: null as unknown as 'index' },
    elements: { point: { radius: customRadius } },
    plugins: {
      tooltip: { enabled: false },
      annotation: { annotations },
      datalabels: {
        display: true,
        borderWidth: 1,
        borderRadius: 10,
        padding: 10,
        borderColor: theme.graph.colors.primaryLine,
        backgroundColor: '#fff',
        color: theme.palette.grey[800],
        formatter: customLabels,
      },
      zoom: {
        limits: {
          x: {
            min: smallestX,
            max: biggestX,
          },
        },
        pan: {
          mode: 'x',
          enabled: enablePan,
          onPanComplete: ({ chart }) => {
            const scale = chart.scales.x;
            const min = scale.min;
            const max = scale.max;

            if (typeof min === 'number' && typeof max === 'number') {
              setLimits(min, max);
            }
            chart.update();
          },
        },
        zoom: {
          mode: 'x',
          wheel: {
            enabled: enableWheelZoom,
          },
          onZoomComplete: ({ chart }) => {
            const scale = chart.scales.x;
            const min = scale.min;
            const max = scale.max;

            if (typeof min === 'number' && typeof max === 'number') {
              setLimits(min, max);
            }
            chart.update();
          },
        },
      },
    },
  };
};

export function customLabels(
  value: LabelValue,
  _context: Context,
): string | null {
  if (value.interpolated) {
    const roundedX = Math.round(value.x * 100) / 100;
    const roundedY = Math.round(value.y * 100) / 100;
    return `P${roundedY}=${roundedX}`;
  }
  return null;
}

function getXAxisLabels(
  smallest: number,
  biggest: number,
  availableTicks?: number[],
): number[] {
  return !availableTicks
    ? defaultTicks(smallest, biggest)
    : generateTicks(smallest, biggest, availableTicks);
}

function defaultTicks(smallest: number, biggest: number): number[] {
  const labels = [];

  if (smallest <= 0 && biggest >= 0) {
    labels.push(0);
  }
  if (smallest <= 1 && biggest >= 2) {
    labels.push(1);
  }
  if (smallest <= 10 && biggest >= 20) {
    labels.push(10);
  }
  if (smallest <= 100 && biggest >= 200) {
    labels.push(100);
  }
  if (smallest <= 1000 && biggest >= 2000) {
    labels.push(1000);
  }

  const insertSmallestIndex = labels.findIndex((v) => v > smallest) || 0;
  if (insertSmallestIndex !== 0) {
    labels.splice(insertSmallestIndex, 0, smallest);
  } else {
    labels.push(smallest);
  }
  labels.push(biggest);
  return labels;
}

function generateTicks(
  smallest: number,
  biggest: number,
  availableTicks: number[],
): number[] {
  const labels: number[] = [];

  if (availableTicks.length > 0) {
    availableTicks = [...availableTicks].sort((a, b) => a - b);
  }

  let prevTick = Math.max(availableTicks[0], smallest);
  labels.push(prevTick);

  for (let i = 0; i < availableTicks.length; i++) {
    const tick = availableTicks[i];
    if (tick >= prevTick && prevTick * 1.5 <= tick && tick <= biggest) {
      labels.push(tick);
      prevTick = tick;
    } else if (tick > biggest) {
      labels.push(tick);
      break;
    }
  }

  const lastValue = labels[labels.length - 1];
  if (lastValue < biggest) {
    const relDiff = (biggest - lastValue) / biggest;
    if (relDiff < 0.2) {
      labels[labels.length - 1] = biggest;
    } else {
      labels.push(biggest);
    }
  }

  return labels;
}

export function customRadius(): number {
  return 0;
}
